<template>
  <div class="tw-flex tw-flex-col tw-gap-3">
    <div class="tw-grid tw-grid-cols-2 tw-items-start tw-gap-2">
      <ColumnFieldGroup
        :fields="headerFields"
        :values="column.values"
        :availability="availability"
        @update="onFieldUpdate"
      />
    </div>

    <div class="tw-grid tw-grid-cols-2 tw-items-start tw-gap-2">
      <div :class="operandCardClasses" data-testid="difference-operand-a">
        <span :class="[operandBadgeClasses, operandBadgeAClasses]">
          {{ t('difference.value_a') }}
        </span>
        <div class="tw-flex tw-flex-col tw-gap-2">
          <ColumnFieldGroup
            :fields="operandAFields"
            :values="column.values"
            :availability="availability"
            @update="onFieldUpdate"
          />
        </div>
      </div>

      <div :class="operandCardClasses" data-testid="difference-operand-b">
        <span :class="[operandBadgeClasses, operandBadgeBClasses]">
          {{ t('difference.value_b') }}
        </span>
        <div class="tw-flex tw-flex-col tw-gap-2">
          <ColumnFieldGroup
            :fields="operandBFields"
            :values="column.values"
            :availability="availability"
            @update="onFieldUpdate"
          />
        </div>
      </div>
    </div>

    <p
      v-if="caption"
      class="tw-m-0 tw-text-xs tw-text-sf-neutral-light-2"
      data-testid="difference-caption"
    >
      {{ caption }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { FieldAvailability } from '../column-constraints'
import type { EditorColumn, FieldSchema } from '../types'
import { SHOW_AS_FIELD_KEY, showAsPresentation } from '../column-presentation'
import { useColumnEditorI18n } from '../use-column-editor-i18n'
import ColumnFieldGroup from './column-field-group.vue'

// The expanded form of a difference column: it compares two values (B − A), so
// the two operands render as their own labeled cards rather than as the flat
// grid the other column types use.
const props =
  defineProps<{
    column: EditorColumn
    fields: FieldSchema[]
    // Computed for the whole column by the row above: an operand being actuals
    // plus budget narrows the basis field that sits above both operands.
    availability: FieldAvailability
  }>()

const emit =
  defineEmits<{
    update: [key: string, value: string]
  }>()

const t = useColumnEditorI18n()

const onFieldUpdate = (key: string, value: string) => emit('update', key, value)

// Groupings taken from the schema rather than from a hardcoded list of field
// keys: anything the server didn't assign to an operand renders above the
// cards, so a field added to the schema can't silently disappear.
const operandAFields = computed(() =>
  props.fields.filter((field) => field.group === 'a')
)
const operandBFields = computed(() =>
  props.fields.filter((field) => field.group === 'b')
)
const headerFields = computed(() =>
  props.fields.filter((field) => field.group !== 'a' && field.group !== 'b')
)

// Explains the subtraction direction, updating with the "show as" choice. A
// mode we have no presentation for gets no caption rather than a wrong one.
const caption = computed(() => {
  const presentation = showAsPresentation(
    props.column.values[SHOW_AS_FIELD_KEY]
  )
  return presentation && t(presentation.captionKey)
})

const operandCardClasses =
  'tw-flex tw-min-w-0 tw-flex-col tw-gap-2 tw-rounded-lg tw-bg-sf-neutral-subtle-1 tw-p-2.5'

const operandBadgeClasses =
  'tw-inline-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-full tw-text-[10px] tw-font-bold'

// A is filled and B is outlined, so the two sides of the comparison read apart
// at a glance without the letters having to be read.
const operandBadgeAClasses = 'tw-bg-sf-info tw-text-white'

const operandBadgeBClasses =
  'tw-border tw-border-solid tw-border-sf-neutral-lighter-2 tw-bg-white tw-text-sf-neutral-light-2'
</script>
